import type { ScanOutcome, ScanState } from '@/graphql/generated/graphql'
import type { Tone } from './tone'
import { severityTone } from './tone'

/**
 * The one-line answer at the top of a report.
 *
 * This file is the reason the whole application exists, so it is worth being
 * precise about what it may and may not say.
 *
 * The schema states that a null `worstFailingSeverity` is "the only thing in
 * this schema that means clean, and it means it only when `Scan.outcome` is
 * COMPLETE and `Scan.errors` is empty". Three further things have to hold
 * before this function is allowed to print a clean verdict, and all four are
 * checked in one place so that no call site can assemble a false pass out of
 * parts that are individually true:
 *
 *   - nothing failed
 *   - nothing came back UNKNOWN, because a check that could not decide has not
 *     decided in the target's favour
 *   - no check failed to run at all
 *   - the scan reached the end of the catalogue
 *
 * Anything short of all four is `inconclusive`, never `clean`.
 *
 * So is a report with no findings at all. An empty report satisfies all four
 * tests vacuously, which makes it the purest false pass available, and it is
 * checked for separately below.
 *
 * An unrecognized `ScanOutcome` fails the `=== 'COMPLETE'` test on its own.
 * That is the conservative default the schema asks for and it costs nothing to
 * get right.
 */
export type VerdictKind = 'pending' | 'running' | 'failing' | 'inconclusive' | 'clean' | 'gone'

export interface Verdict {
  readonly kind: VerdictKind
  readonly tone: Tone
  /** Short enough to be the largest text on the page. */
  readonly headline: string
  /** The precise sentence. Always present, always says what the headline elides. */
  readonly detail: string
}

export interface VerdictInput {
  readonly state: ScanState
  readonly outcome: ScanOutcome | null | undefined
  readonly summary: {
    readonly total: number
    readonly failed: number
    readonly unknown: number
    readonly passed: number
    readonly worstFailingSeverity: string | null | undefined
  }
  readonly errorCount: number
}

/** True only for a scan that ran every check to the end. */
export function isComplete(outcome: ScanOutcome | null | undefined): boolean {
  return outcome === 'COMPLETE'
}

/**
 * Whether the "this scan did not finish" banner is due.
 *
 * Separate from the verdict on purpose. A scan that was cut short after finding
 * three critical failures should headline the critical failures and carry the
 * banner beside them, rather than collapsing both into one weaker statement.
 */
export function isTruncated(state: ScanState, outcome: ScanOutcome | null | undefined): boolean {
  return state === 'FINISHED' && !isComplete(outcome)
}

const TRUNCATION_REASON: Readonly<Record<string, string>> = {
  BUDGET_EXCEEDED:
    'It ran out of the thirty seconds it is given and was cut off. The findings below are real; the checks that never reported are unknown, not absent.',
  CANCELLED:
    'It was cancelled before every check reported. The findings below are real; the checks that never reported are unknown, not absent.',
  FAILED:
    'It broke for a reason on our side rather than the target’s. The findings below are real; the checks that never reported are unknown, not absent.',
}

export function truncationReason(outcome: ScanOutcome | null | undefined): string {
  const known = outcome ? TRUNCATION_REASON[outcome] : undefined
  return (
    known ??
    'It stopped before every check reported. The findings below are real; the checks that never reported are unknown, not absent.'
  )
}

/**
 * The headline for a failing scan, keyed on the worst severity.
 *
 * Written out per severity rather than assembled as `${label} issues found`,
 * which produces "High issues found" and reads like a typo. The wording also
 * has to stay honest at the bottom of the range: a report whose worst failure
 * is INFO has genuinely not found anything alarming, and saying so is better
 * than dressing it up.
 */
const FAILING_HEADLINE: Readonly<Record<string, string>> = {
  CRITICAL: 'Critical issues found',
  HIGH: 'High-severity issues found',
  MEDIUM: 'Medium-severity issues found',
  LOW: 'Low-severity issues found',
  INFO: 'Minor issues found',
}

function countPhrase(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`
}

/** The gaps in a report, written out, or an empty string when there are none. */
function gapSentence(unknown: number, errorCount: number): string {
  const parts: string[] = []
  if (unknown > 0) parts.push(`${countPhrase(unknown, 'check', 'checks')} could not reach a verdict`)
  if (errorCount > 0) parts.push(`${countPhrase(errorCount, 'check', 'checks')} could not run at all`)
  return parts.join(' and ')
}

export function verdictFor(input: VerdictInput): Verdict {
  const { state, outcome, summary, errorCount } = input

  if (state === 'QUEUED') {
    return {
      kind: 'pending',
      tone: 'neutral',
      headline: 'Queued',
      detail: 'The scan has been accepted and is waiting for a slot. Results appear as they land.',
    }
  }

  if (state !== 'FINISHED') {
    return {
      kind: 'running',
      tone: 'neutral',
      headline: 'Scanning',
      detail: `${countPhrase(summary.total, 'check has', 'checks have')} reported so far. This page updates as the rest arrive.`,
    }
  }

  if (summary.failed > 0) {
    const worst = summary.worstFailingSeverity
    const gaps = gapSentence(summary.unknown, errorCount)
    return {
      kind: 'failing',
      tone: worst ? severityTone(worst as never) : 'high',
      headline: worst ? (FAILING_HEADLINE[worst] ?? 'Issues found') : 'Issues found',
      detail: gaps
        ? `${countPhrase(summary.failed, 'check', 'checks')} failed. On top of that, ${gaps}.`
        : `${countPhrase(summary.failed, 'check', 'checks')} failed out of ${summary.total}.`,
    }
  }

  const gaps = gapSentence(summary.unknown, errorCount)

  // A report with nothing in it satisfies "nothing failed, nothing unknown,
  // nothing failed to run" vacuously, and would otherwise print the one
  // headline in this file that means the target is fine. A scan that produced
  // no findings has not established anything about anything.
  if (summary.total === 0) {
    return {
      kind: 'inconclusive',
      tone: 'unknown',
      headline: 'This scan produced no results',
      detail:
        'Not one check reported. That is not a clean bill of health, it means nothing was tested. Running the scan again is worth a try.',
    }
  }

  if (gaps || !isComplete(outcome)) {
    return {
      kind: 'inconclusive',
      tone: 'unknown',
      headline: 'Nothing failed, but this is not a clean bill of health',
      detail: gaps
        ? `No check reported a failure, but ${gaps}. Whatever those would have tested is untested.`
        : 'No check reported a failure, but the scan did not get through the whole catalogue, so some checks never ran.',
    }
  }

  return {
    kind: 'clean',
    tone: 'pass',
    headline: 'No issues found',
    detail: `All ${summary.total} checks ran and every one of them passed.`,
  }
}

/** The verdict for an id the server has no scan for. */
export const GONE_VERDICT: Verdict = {
  kind: 'gone',
  tone: 'neutral',
  headline: 'This scan is no longer available',
  detail:
    'Results are kept for a limited time and then forgotten. The link may also be mistyped. Running the scan again gives a fresh report.',
}
